import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { finalize } from "rxjs/operators";
import {
  AsistenciaDia,
  Dispositivo,
  EstatusAsistencia,
} from "src/app/shared/model/asistencia/asistencia.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../asistencia.service";
import { soloFecha, soloHora } from "../formato-fecha";

/**
 * Mi asistencia.
 *
 * La ve cualquier empleado, tambien el de oficina. Responde tres cosas y
 * nada mas: a que hora se me esperaba, a que hora llegue y como quedo el
 * dia.
 *
 * LA TERCERA LA DECIDE LA EMPRESA
 * Con «Mostrar al empleado el veredicto de su dia» apagado, el servidor
 * manda las horas sin el veredicto y veredictoVisible=false. Entonces no
 * se pinta ni la etiqueta de estatus ni el retardo: una etiqueta vacia
 * saldria como "-", que parece un error, y cualquier valor por omision
 * ("En curso") a quien ya cerro su jornada le diria algo falso.
 *
 * POR QUE NO SE REGISTRA DESDE AQUI
 * El registro remoto exige un dispositivo vinculado y la biometria del
 * propio telefono. Un navegador de escritorio no tiene ninguna de las
 * dos, asi que poner el boton aqui prometeria algo que el servidor va a
 * rechazar. Esta pantalla consulta; quien registra es la app.
 */
@Component({
  selector: "app-mi-asistencia",
  standalone: true,
  templateUrl: "./mi-asistencia.component.html",
  styleUrls: ["./mi-asistencia.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
})
export class MiAsistenciaComponent implements OnInit {
  dia: AsistenciaDia = null;
  dispositivos: Dispositivo[] = [];
  fecha: Date = new Date();
  loading = false;

  constructor(
    private asistenciaService: AsistenciaService,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    queueMicrotask(() => {
      this.consultar();
      this.cargarDispositivos();
      this.cdr.detectChanges();
    });
  }

  consultar(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.asistenciaService
      .miDia(this.aIsoFecha(this.fecha))
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          this.dia = respuesta;
        },
        error: () => {
          this.dia = null;
          this.msg.errorCall("No se pudo consultar su asistencia.", null, null);
        },
      });
  }

  hoy(): void {
    this.fecha = new Date();
    this.consultar();
  }

  /**
   * Solo un false explicito lo apaga: un servidor anterior al parametro no
   * manda la bandera y siempre mostraba el veredicto.
   */
  get veredictoVisible(): boolean {
    return this.dia?.veredictoVisible !== false;
  }

  /** El retardo es parte del veredicto: se calla junto con la etiqueta. */
  get mostrarRetardo(): boolean {
    return this.veredictoVisible && this.dia?.minutosRetardo > 0;
  }

  /** El subtitulo no promete "como quedo el dia" si no se va a decir. */
  get subtitulo(): string {
    return this.dia && !this.veredictoVisible
      ? "A qué hora se le esperaba y a qué hora llegó."
      : "A qué hora se le esperaba, a qué hora llegó y cómo quedó el día.";
  }

  revocarDispositivo(dispositivo: Dispositivo): void {
    this.msg.questionCall(
      `¿Desvincular el equipo ${dispositivo.modelo || dispositivo.plataforma}?`,
      null,
      (respuesta: any) => {
        if (!respuesta?.value) {
          return;
        }
        this.asistenciaService.revocarDispositivo(dispositivo).subscribe({
          next: () => {
            this.msg.successCall("El equipo quedó desvinculado.", null, null);
            this.cargarDispositivos();
          },
          error: () => this.msg.errorCall("No se pudo desvincular el equipo.", null, null),
        });
      }
    );
  }

  etiquetaEstatus(estatus: EstatusAsistencia): string {
    const etiquetas: Record<string, string> = {
      PUNTUAL: "Puntual",
      RETARDO: "Retardo",
      FALTA: "Falta",
      SIN_HORARIO: "Sin horario asignado",
      DESCANSO: "Descanso",
      VACACIONES: "Vacaciones",
      PERMISO: "Permiso",
      JUSTIFICADO: "Justificado",
      PENDIENTE: "En curso",
    };
    return etiquetas[estatus] ?? estatus ?? "-";
  }

  claseEstatus(estatus: EstatusAsistencia): string {
    // Modificadores globales de table-page__status-badge (styles.css):
    // is-danger / is-pending / is-active / is-inactive.
    if (estatus === "FALTA") {
      return "table-page__status-badge is-danger";
    }
    if (estatus === "RETARDO" || estatus === "PENDIENTE") {
      // "En curso" es un dia sin veredicto todavia: tono de pendiente, no
      // el gris de inactivo que se reserva para descanso/vacaciones/permiso.
      return "table-page__status-badge is-pending";
    }
    if (estatus === "PUNTUAL" || estatus === "JUSTIFICADO") {
      return "table-page__status-badge is-active";
    }
    return "table-page__status-badge is-inactive";
  }

  etiquetaModalidad(): string {
    if (!this.dia) {
      return "-";
    }
    const am = this.nombreModalidad(this.dia.modalidadAm);
    const pm = this.nombreModalidad(this.dia.modalidadPm);
    if (!am && !pm) {
      return "Sin asignar";
    }
    if (!pm || am === pm) {
      return am;
    }
    return `${am} por la mañana, ${pm} por la tarde`;
  }

  etiquetaEstatusDispositivo(estatus: string): string {
    const etiquetas: Record<string, string> = {
      PENDIENTE: "Esperando aprobación",
      ACTIVO: "Activo",
      REVOCADO: "Revocado",
    };
    return etiquetas[estatus] ?? estatus;
  }

  soloHora(valor: string | Date): string {
    return soloHora(valor);
  }

  soloFecha(valor: string | Date): string {
    return soloFecha(valor);
  }

  get horasTrabajadas(): string {
    const minutos = this.dia?.minutosTrabajados;
    if (!minutos && minutos !== 0) {
      return "-";
    }
    const horas = Math.floor(minutos / 60);
    const resto = minutos % 60;
    return `${horas} h ${`${resto}`.padStart(2, "0")} min`;
  }

  // ------------------------------------------------------------------

  private cargarDispositivos(): void {
    this.asistenciaService.consultarDispositivos(null).subscribe({
      next: (respuesta) => {
        this.dispositivos = respuesta?.list ?? [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.dispositivos = [];
      },
    });
  }

  /** Partes locales: toISOString() restaria horas y cambiaria el dia. */
  private aIsoFecha(fecha: Date): string {
    if (!fecha) {
      return null;
    }
    const mes = `${fecha.getMonth() + 1}`.padStart(2, "0");
    const dia = `${fecha.getDate()}`.padStart(2, "0");
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  }

  private nombreModalidad(modalidad: string): string {
    const nombres: Record<string, string> = {
      OFICINA: "Oficina",
      HOME_OFFICE: "Home office",
      MIXTO: "Mixto",
    };
    return modalidad ? nombres[modalidad] ?? modalidad : "";
  }
}
